/**
 * ring-texture.js — 罗盘文字环贴图生成
 *
 * 纯 Canvas2D，不依赖 DOM，也不依赖 three。
 * 浏览器里传 document.createElement('canvas')，Node 里传 node-canvas，
 * 所以这一层可以在没有 GPU 的环境里离线渲染成 PNG 来肉眼校对。
 *
 * 生成的贴图是正方形、透明底，中心对齐，直接当 THREE.CanvasTexture 用。
 */

import { BRANCHES, STEMS } from '../src/ganzhi.js';
import { buildHexagram, PALACE_INDEX, TRIGRAM_BITS } from '../src/hexagram.js';
import { XIU_DEGREES } from '../src/xiu-degrees.js';
import {
  RING_LAYOUT, RING_STYLE, TAIJI_RADIUS, TAIJI_STYLE, TRIGRAM_GLYPH
} from './tuning.js';
import { FLYING_STAR_RING_PALACES } from './feixing-view.js';

export const PALETTE = {
  white: '#ffffff',
  jade: '#7fe3c0',
  jadeBright: '#dffbee',
  jadeDim: 'rgba(127,227,192,0.34)',
  gold: '#ffe98a',
  goldDim: 'rgba(255,233,138,0.4)',
  ink: '#05080a'
};

/**
 * @param {object} o
 *   ctx2d        一个 CanvasRenderingContext2D（由调用方创建，尺寸 size×size）
 *   size         画布边长（像素），建议 1024 或 2048
 *   tokens       环上的字符数组；对象 token 可带 span（度数）
 *   inner/outer  环的内外半径，取 0–0.5 的归一化值（0.5 = 触及画布边缘）
 *   offset       起始角度（弧度）
 *   fontFamily   字体
 *   color        文字与线条颜色
 *   glow         发光半径（像素），0 为不发光
 *   weight       线宽
 *   fontScale    字号相对环宽的比例
 */
function uprightRotation(angle) {
  let rotation = angle + Math.PI / 2;
  const normalized = ((rotation + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (normalized > Math.PI / 2) rotation -= Math.PI;
  if (normalized < -Math.PI / 2) rotation += Math.PI;
  return rotation;
}

function withGlyphTransform(ctx, angle, radius, draw) {
  ctx.save();
  ctx.translate(Math.cos(angle) * radius, Math.sin(angle) * radius);
  ctx.rotate(uprightRotation(angle));
  draw();
  ctx.restore();
}

/**
 * 用三条几何爻线绘制八卦符号，不依赖字体中的 Unicode ☰–☷。
 * bits 自下而上排列：1 为阳爻，0 为阴爻。
 */
export function drawTrigramGlyph(ctx, x, y, w, h, bits) {
  if (!Array.isArray(bits) || bits.length !== 3) {
    throw new TypeError('drawTrigramGlyph bits 必须是自下而上的三爻数组');
  }
  const barHeight = h * TRIGRAM_GLYPH.lineWidthRatio;
  const spacing = h * TRIGRAM_GLYPH.lineSpacingRatio;
  const yinGap = w * TRIGRAM_GLYPH.yinGapRatio;
  ctx.save();
  ctx.fillStyle = ctx.strokeStyle;
  bits.forEach((yang, index) => {
    const centerY = y + h / 2 + (1 - index) * spacing;
    const top = centerY - barHeight / 2;
    if (yang) {
      ctx.fillRect(x, top, w, barHeight);
    } else {
      const half = (w - yinGap) / 2;
      ctx.fillRect(x, top, half, barHeight);
      ctx.fillRect(x + half + yinGap, top, half, barHeight);
    }
  });
  ctx.restore();
}

function drawToken(ctx, token, angle, radius, fontSize, mode = 'stroke') {
  if (token && typeof token === 'object' && Array.isArray(token.bits)) {
    withGlyphTransform(ctx, angle, radius, () => {
      const glyphWidth = fontSize * TRIGRAM_GLYPH.widthScale;
      const glyphHeight = fontSize * TRIGRAM_GLYPH.heightScale;
      drawTrigramGlyph(
        ctx,
        -glyphWidth / 2,
        -glyphHeight / 2,
        glyphWidth,
        glyphHeight,
        token.bits
      );
    });
    return;
  }
  const displayToken = token && typeof token === 'object'
    ? (token.text ?? token.label ?? token)
    : token;
  const chars = [...String(displayToken)];
  const gap = fontSize * RING_STYLE.radialTextGap;
  const startRadius = radius - ((chars.length - 1) * gap) / 2;
  chars.forEach((ch, index) => {
    withGlyphTransform(ctx, angle, startRadius + index * gap, () => {
      if (mode === 'fill') ctx.fillText(ch, 0, 0);
      else ctx.strokeText(ch, 0, 0);
    });
  });
}

/** 运行时高亮与测试共用的 token → 格位解析，卦符对象以 label 对齐卦名。 */
export function ringCellIndex(tokens, token) {
  return tokens.findIndex((candidate) => (
    String(candidate?.label ?? candidate) === String(token?.label ?? token)
  ));
}

/**
 * 把 token 解析为环上的起止角和中心角。
 *
 * - 全部 token 不带 span：沿用等分。
 * - 只要出现 span：每个 token 都必须给出正的 span，不默默填补。
 * - span 是该数据表自己的「度」；360 今度或 365.25 古度都会
 *   按总和归一到一整圆，不在渲染层擅自换算或平均。
 *
 * offset 继续表示第一个 token 的中心角，与旧等分实现兼容。
 */
export function ringTokenSectors(tokens, offset = 0) {
  if (!Array.isArray(tokens) || tokens.length === 0) return [];
  const hasCustomSpan = tokens.some((token) => token && typeof token === 'object'
    && Object.hasOwn(token, 'span'));
  const spans = hasCustomSpan
    ? tokens.map((token, index) => {
      const span = token && typeof token === 'object' ? Number(token.span) : NaN;
      if (!Number.isFinite(span) || span <= 0) {
        throw new RangeError(`tokens[${index}].span 必须是正数；自定义宿度不允许与等分混用`);
      }
      return span;
    })
    : tokens.map(() => 1);
  const total = spans.reduce((sum, span) => sum + span, 0);
  const radiansPerUnit = (Math.PI * 2) / total;
  let cursor = offset - spans[0] * radiansPerUnit / 2;
  return tokens.map((token, index) => {
    const angularSpan = spans[index] * radiansPerUnit;
    const sector = {
      token,
      span: spans[index],
      spanTotal: total,
      start: cursor,
      center: cursor + angularSpan / 2,
      end: cursor + angularSpan
    };
    cursor = sector.end;
    return sector;
  });
}

export function drawRing(o) {
  const {
    ctx, size, tokens, inner, outer,
    offset = 0,
    fontFamily = RING_STYLE.fontFamily,
    color = PALETTE.white,
    glow = size * RING_STYLE.glowRatio,
    weight = 1.1,
    fontScale = 0.62,
    textOpacity = RING_STYLE.textOpacity,
    drawEdges = true
  } = o;

  const c = size / 2;
  const rIn = inner * size;
  const rOut = outer * size;
  const rMid = (rIn + rOut) / 2;
  const band = rOut - rIn;

  ctx.save();
  ctx.translate(c, c);
  ctx.lineCap = 'round';

  if (glow > 0) {
    ctx.shadowColor = color;
    ctx.shadowBlur = glow;
  }

  // 静态环只有内外两条主圆弧线；字与字之间不画格线，也没有底色。
  if (drawEdges) {
    ctx.strokeStyle = color;
    ctx.lineWidth = RING_STYLE.edgeWeight;
    ctx.globalAlpha = RING_STYLE.edgeOpacity;
    for (const r of [rIn, rOut]) {
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.lineWidth = RING_STYLE.detailLineWeight;
    ctx.globalAlpha = RING_STYLE.detailLineOpacity;
    for (const inset of RING_STYLE.detailLineOffsets) {
      for (const r of [rIn + band * inset, rOut - band * inset]) {
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  }

  // 文字直接浮在圆弧线之间，不绘制任何格底或字间分隔。
  if (tokens && tokens.length) {
    const sectors = ringTokenSectors(tokens, offset);
    const fontSize = band * fontScale;
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.strokeStyle = color;
    ctx.lineWidth = fontSize * RING_STYLE.textStrokeRatio;
    ctx.globalAlpha = textOpacity;
    sectors.forEach(({ token, center: angle }) => {
      const tokenColor = token && typeof token === 'object' ? token.color : null;
      if (tokenColor) {
        ctx.strokeStyle = tokenColor;
        ctx.fillStyle = tokenColor;
      } else {
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
      }
      drawToken(ctx, token, angle, rMid, fontSize);
    });
    ctx.globalAlpha = 1;
  }

  ctx.restore();
}

/*
 * 旧版曾在这里画逐格径向刻度；高清参考确认静态盘面没有格子，
 * 因此格框只允许出现在 drawRingHighlights() 的已点亮单元上。
 */

/**
 * 在透明画布上只画被选中的描边格。Field 将它叠在静态底图之上；
 * items: [{ token, kind, color, boost, dashed }]
 * connections: [{ from, to, color }]
 */
export function drawRingHighlights(o) {
  const {
    ctx, size, spec, items = [], connections = [],
    fontFamily = RING_STYLE.fontFamily
  } = o;
  const { tokens, inner, outer, offset = 0, fontScale, weight } = spec;
  if (!tokens?.length || (!items.length && !connections.length)) return;

  const center = size / 2;
  const rIn = inner * size;
  const rOut = outer * size;
  const rMid = (rIn + rOut) / 2;
  const band = rOut - rIn;
  const sectors = ringTokenSectors(tokens, offset);
  const fontSize = band * fontScale;
  const indexOf = (token) => ringCellIndex(tokens, token);

  ctx.save();
  ctx.translate(center, center);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.font = `${fontSize}px ${fontFamily}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (const item of items) {
    const index = indexOf(item.token);
    if (index < 0) continue;
    const { center: angle, start, end } = sectors[index];
    const color = item.color || PALETTE.jadeBright;
    const boost = item.boost || 1;

    ctx.shadowColor = color;
    ctx.shadowBlur = size * RING_STYLE.glowRatio;
    ctx.fillStyle = color;
    ctx.globalAlpha = item.dashed ? 0 : item.fillOpacity * boost;
    ctx.beginPath();
    ctx.arc(0, 0, rOut, start, end);
    ctx.arc(0, 0, rIn, end, start, true);
    ctx.closePath();
    ctx.fill();

    ctx.setLineDash(item.dashed ? item.dash : []);
    ctx.strokeStyle = color;
    ctx.lineWidth = weight * item.lineWidthScale;
    ctx.globalAlpha = item.strokeOpacity * boost;
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.strokeStyle = color;
    ctx.lineWidth = fontSize * RING_STYLE.textStrokeRatio * item.lineWidthScale;
    ctx.globalAlpha = item.textOpacity * boost;
    drawToken(ctx, tokens[index], angle, rMid, fontSize);
  }

  for (const connection of connections) {
    const fromIndex = indexOf(connection.from);
    const toIndex = indexOf(connection.to);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) continue;
    const from = sectors[fromIndex].center;
    const to = sectors[toIndex].center;
    const linkRadius = rIn + band * connection.inset;
    let delta = ((to - from + Math.PI) % (Math.PI * 2)) - Math.PI;
    if (delta <= -Math.PI) delta += Math.PI * 2;
    ctx.strokeStyle = connection.color || PALETTE.gold;
    ctx.lineWidth = weight * connection.lineWidthScale;
    ctx.globalAlpha = connection.opacity;
    ctx.shadowColor = ctx.strokeStyle;
    ctx.shadowBlur = size * RING_STYLE.glowRatio;
    ctx.beginPath();
    ctx.arc(0, 0, linkRadius, from, from + delta, delta < 0);
    ctx.stroke();
  }

  ctx.restore();
}

/** 太极图：阴阳鱼，中心发光 */
export function drawTaiji(o) {
  const { ctx, size, color = PALETTE.white, radius = 0.4 } = o;
  const c = size / 2;
  const r = radius * size;
  const halfR = r / 2;
  const eyeR = r * TAIJI_STYLE.eyeRadiusRatio;
  const strokeWidth = r * TAIJI_STYLE.strokeWidthRatio;
  ctx.save();
  ctx.translate(c, c);
  ctx.rotate(-Math.PI / 2);

  // 阴阳两半仍严格等面积，但改为半透明铺色，让太极成为发光线稿而非实体贴片。
  ctx.fillStyle = TAIJI_STYLE.darkColor;
  ctx.globalAlpha = TAIJI_STYLE.yinAlpha;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();

  // 阳鱼：外圆半径 r；两枚 S 形小圆半径严格为 r/2，圆心在 (0, ±r/2)。
  ctx.fillStyle = color;
  ctx.globalAlpha = TAIJI_STYLE.yangAlpha;
  ctx.beginPath();
  ctx.arc(0, 0, r, -Math.PI / 2, Math.PI / 2);
  ctx.arc(0, halfR, halfR, Math.PI / 2, -Math.PI / 2, true);
  ctx.arc(0, -halfR, halfR, Math.PI / 2, -Math.PI / 2);
  ctx.fill();

  // 外圆与 S 形都用同一支细光线；shadow 只作用于线，不把内部重新烧实。
  ctx.globalAlpha = TAIJI_STYLE.strokeAlpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = strokeWidth;
  ctx.shadowColor = color;
  ctx.shadowBlur = r * TAIJI_STYLE.glowBlur;
  ctx.beginPath();
  ctx.arc(0, 0, r - strokeWidth / 2, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(0, halfR, halfR, Math.PI / 2, -Math.PI / 2, true);
  ctx.arc(0, -halfR, halfR, Math.PI / 2, -Math.PI / 2);
  ctx.stroke();

  // 两个鱼眼是唯一保持实心的部分，且严格等大。
  ctx.shadowBlur = 0;
  ctx.globalAlpha = 1;
  ctx.beginPath();
  ctx.arc(0, -halfR, eyeR, 0, Math.PI * 2);
  ctx.fillStyle = TAIJI_STYLE.darkColor;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(0, halfR, eyeR, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();

  // 外圈
  ctx.strokeStyle = color;
  ctx.lineWidth = size * 0.004;
  ctx.globalAlpha = 0.8;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** 六爻卦象：六道横线，阳实阴断，可标动爻 */
export function drawHexagramGlyph(o) {
  const {
    ctx, x, y, width, height, lines,
    moving = [], color = PALETTE.gold, movingColor = '#ff8f6b'
  } = o;
  const rows = 6;
  const gap = height / (rows * 1.9);
  const bar = (height - gap * (rows - 1)) / rows;
  const split = width * 0.22;
  ctx.save();
  lines.forEach((yang, i) => {
    const isMoving = moving.includes(i + 1);
    ctx.fillStyle = isMoving ? movingColor : color;
    ctx.shadowColor = ctx.fillStyle;
    ctx.shadowBlur = isMoving ? bar * 1.6 : bar * 0.5;
    // 自下而上：初爻在最下
    const top = y + (rows - 1 - i) * (bar + gap);
    if (yang) {
      ctx.fillRect(x, top, width, bar);
    } else {
      ctx.fillRect(x, top, (width - split) / 2, bar);
      ctx.fillRect(x + (width + split) / 2, top, (width - split) / 2, bar);
    }
  });
  ctx.restore();
}

/**
 * 六层环的规格。
 *
 * 每一层是独立贴图 + 独立 Mesh：贴图里环占 0.30–0.46，实际半径由 radius 控制。
 * 这样每层都能吃满贴图分辨率，外圈的节气小字才不会糊。
 *
 * radius 是相对最外圈的比例，实测自参考画面的径向亮度剖面
 * （峰值 px：58 / 111–132 / 219–248 / 310–367 / 427–490 / 537–621）。
 */
const PALACE_ORDER = ['乾', '兑', '离', '震', '巽', '坎', '艮', '坤'];
/** 后天八卦方位：屏幕正上为南，按屏幕顺时针排列。 */
const TRIGRAM_COMPASS_ORDER = ['离', '坤', '兑', '乾', '坎', '艮', '震', '巽'];
const EIGHT_PALACE_HEXAGRAMS = Object.entries(PALACE_INDEX)
  .map(([bits, info]) => ({
    info,
    hexagram: buildHexagram([...bits].map(Number))
  }))
  .sort((a, b) =>
    PALACE_ORDER.indexOf(a.info.palace) - PALACE_ORDER.indexOf(b.info.palace)
    || a.info.rank - b.info.rank
  )
  .map(({ hexagram }) => hexagram);

const RING_CONTENT = {
  trigramSymbols: TRIGRAM_COMPASS_ORDER.map((name) => ({
    label: name,
    bits: Array.from({ length: 3 }, (_, index) => (TRIGRAM_BITS[name] >> index) & 1)
  })),
  trigrams: TRIGRAM_COMPASS_ORDER,
  // 运行时由 feixing-view.js 重绘为「宫名+星数+星名」；这里仅保留方位键。
  nineStars: FLYING_STAR_RING_PALACES,
  // 奇门九星与八门目前仅用于补全罗盘形制，不参与六爻排盘。
  // 日后若加入奇门遁甲，须另起独立计算模块，不得从这两组展示 token 反推盘局。
  qimenStars: ['天蓬', '天芮', '天冲', '天辅', '天禽', '天心', '天柱', '天任', '天英'],
  gates: ['休', '生', '伤', '杜', '景', '死', '惊', '开'],
  stems: STEMS,
  branches: BRANCHES,
  mountains: '壬子癸丑艮寅甲卯乙辰巽巳丙午丁未坤申庚酉辛戌乾亥'.split(''),
  terms: '立春雨水惊蛰春分清明谷雨立夏小满芒种夏至小暑大暑立秋处暑白露秋分寒露霜降立冬小雪大雪冬至小寒大寒'.match(/.{2}/gu),
  // 乾隆甲子黄道宿距（360 今度），不等分；方位锚点采用工程约定，仍待可靠古籍证据。
  mansions: XIU_DEGREES.map(({ name, decimalDegrees }) => ({
    label: name,
    text: name,
    span: decimalDegrees
  })),
  ganzhi: Array.from({ length: 60 }, (_, index) => STEMS[index % 10] + BRANCHES[index % 12]),
  // 64 格保留完整卦名作 label（高亮索引与 HUD 使用），底图只画末字以保证可读。
  hexagrams: EIGHT_PALACE_HEXAGRAMS.map(({ name }) => ({
    label: name,
    text: [...name].at(-1)
  }))
};

export const RING_SPECS = RING_LAYOUT.map((layout) => {
  const tokens = RING_CONTENT[layout.key];
  let offset = layout.startAngle;
  if (layout.anchorMode === 'sector-start') {
    const spans = tokens.map((token) => Number(token?.span));
    if (!spans.every((span) => Number.isFinite(span) && span > 0)) {
      throw new RangeError(`${layout.key} 以格起点锚定时，token 必须全部带正 span`);
    }
    offset += Math.PI * spans[0] / spans.reduce((sum, span) => sum + span, 0);
  }
  return {
    ...layout,
    tokens,
    offset
  };
});

/** 保留既有导出入口；数值统一维护在 tuning.js。 */
export { TAIJI_RADIUS };
